import { useCallback, useEffect, useState } from 'react';
import { api, ROL_ETIKET, type Rol } from '../lib/api';
import { FotoEkle } from './FotoEkle';
import { rolEtiket } from '@mod/domain';
import {
  KalkanIkon,
  KameraIkon,
  KapatIkon,
  OnayIkon,
  UyariIkon,
} from '../lib/ikon';

/**
 * FAZ AKSİYONLARI (devir dokumani Bolum 6.3, 6.4, 6.6).
 *
 * 🔑 GORUNURLUK KURALI: bu uc aksiyon YALNIZ iskele `ayakta` iken gorunur.
 *    Devam eden bir faz varken IKINCI faz acilamaz — kullanicinin acik talebi.
 *
 * Revize ve sokum AYNI SABLONU kullanir, farkli baslik — ogrenme maliyeti sifir
 * (v9 karari: "Revize Emri Aç" sokum ekraniyla ayni sablon).
 */

const REVIZE_TURLERI = [
  { d: 'yukseltme', e: 'Yükseltme' },
  { d: 'alcaltma', e: 'Alçaltma' },
  { d: 'kat_ekleme', e: 'Kat ekleme' },
  { d: 'kat_cikarma', e: 'Kat çıkarma' },
  { d: 'balkon_konsol', e: 'Balkon / konsol' },
  { d: 'diger', e: 'Diğer' },
] as const;

type Mod = 'sokum' | 'revize' | 'kontrol' | null;

export function FazAksiyon({
  jobId,
  oncekiEkip,
  onBitti,
}: {
  jobId: string;
  oncekiEkip: string[];
  onBitti: () => void | Promise<void>;
}) {
  const [mod, setMod] = useState<Mod>(null);

  return (
    <>
      {/* Bölüm 6.3 — alt aksiyon çubuğu, yalnız `ayakta` iken görünür */}
      <div className="mb-5 flex flex-wrap gap-2">
        <button
          onClick={() => setMod('revize')}
          className="rounded-(--radius-mod) border border-stok px-4 py-2 text-sm text-stok"
        >
          Revizyon Talimatı
        </button>
        <button
          onClick={() => setMod('sokum')}
          className="rounded-(--radius-mod) border border-stok px-4 py-2 text-sm text-stok"
        >
          Söküm Talimatı
        </button>
        <button
          onClick={() => setMod('kontrol')}
          className="ml-auto inline-flex items-center gap-1.5 rounded-(--radius-mod) border border-info px-4 py-2 text-sm text-info"
        >
          <OnayIkon boyut={16} /> Haftalık Kontrol
        </button>
      </div>

      {mod === 'kontrol' && (
        <RutinKontrol jobId={jobId} onKapat={() => setMod(null)} onBitti={onBitti} />
      )}
      {(mod === 'sokum' || mod === 'revize') && (
        <FazAc
          jobId={jobId}
          mod={mod}
          oncekiEkip={oncekiEkip}
          onKapat={() => setMod(null)}
          onBitti={onBitti}
        />
      )}
    </>
  );
}

/** Söküm / Revize emri açma — tek şablon, iki mod. */
function FazAc({
  jobId,
  mod,
  oncekiEkip,
  onKapat,
  onBitti,
}: {
  jobId: string;
  mod: 'sokum' | 'revize';
  oncekiEkip: string[];
  onKapat: () => void;
  onBitti: () => void | Promise<void>;
}) {
  const rev = mod === 'revize';
  const [calisanlar, setCalisanlar] = useState<{ id: string; ad: string; rol: Rol }[]>([]);
  const [ekip, setEkip] = useState<string[]>([]);
  const [tur, setTur] = useState<string>('yukseltme');
  const [not, setNot] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);

  useEffect(() => {
    api
      .get<{ calisanlar: { id: string; ad: string; rol: Rol; sicilNo: number }[] }>('/tanimlar')
      .then((d) => setCalisanlar(d.calisanlar.filter((c) => c.sicilNo < 9000)))
      .catch(() => setCalisanlar([]));
  }, []);

  async function gonder() {
    setHata(null);
    setMesgul(true);
    try {
      await api.post(`/isler/${jobId}/${rev ? 'revize-ac' : 'sokum-ac'}`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        ekipIds: ekip,
        ...(rev ? { tur, not: not.trim() || null } : {}),
      });
      await onBitti();
      onKapat();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Oluşturulamadı');
      setMesgul(false);
    }
  }

  return (
    <div className="mb-5 rounded-(--radius-mod) border border-stok/40 bg-white p-4">
      <h3 className="mb-1 font-semibold text-navy">{rev ? 'Revize Emri Aç' : 'Söküm Emri Aç'}</h3>

      {/* Mavi bilgi notu — prototipteki gibi, önceki ekibi hatırlatır */}
      <p className="mb-4 rounded-(--radius-mod) bg-navy/5 p-3 text-sm text-navy">
        <strong>Bir önceki söküm/kuran ekip:</strong> {oncekiEkip.join(', ') || '—'} —{' '}
        {rev ? 'revize' : 'söküm'} için aynı ya da farklı ekip seçebilirsin
        {rev && ' (keşif yok, kurulu iskele üzerinde)'}.
      </p>

      {rev && (
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-ink-2">Revizyon türü</label>
          <select
            className="w-full rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm"
            value={tur}
            onChange={(e) => setTur(e.target.value)}
          >
            {REVIZE_TURLERI.map((t) => (
              <option key={t.d} value={t.d}>
                {t.e}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-4">
        <label className="mb-1 block text-sm font-medium text-ink-2">
          {rev ? 'Revize' : 'Söküm'} ekibi
        </label>
        <div className="space-y-1">
          {calisanlar.map((c) => {
            const secili = ekip.includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => setEkip((p) => (secili ? p.filter((x) => x !== c.id) : [...p, c.id]))}
                className={`flex w-full items-center gap-2 rounded-(--radius-mod) border px-3 py-2 text-left text-sm ${
                  secili ? 'border-navy bg-navy/5' : 'border-neutral-200'
                }`}
              >
                <span className={secili ? 'text-ok' : 'text-faint'}>✓</span>
                <span className="flex-1">{c.ad}</span>
                <span className="text-xs text-wait">{rolEtiket(c.rol)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {rev && (
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-ink-2">
            Not <span className="font-normal text-wait">— opsiyonel</span>
          </label>
          <textarea
            className="w-full rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm"
            rows={2}
            placeholder="Ne değişecek, dikkat notları…"
            value={not}
            onChange={(e) => setNot(e.target.value)}
          />
        </div>
      )}

      {hata && <p className="mb-3 text-sm text-crit">{hata}</p>}

      <div className="flex gap-2">
        <button
          disabled={mesgul || ekip.length === 0}
          onClick={() => void gonder()}
          className="rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {rev ? 'Revize Emrini Oluştur' : 'Söküm Emrini Oluştur'}
        </button>
        <button onClick={onKapat} className="px-3 text-sm text-wait">
          Geri
        </button>
      </div>
    </div>
  );
}

/**
 * HAFTALIK RUTİN KONTROL (Bolum 6.4) — pop-up, uc adim.
 * "geç" secenegi var: kontrol yapildi ama foto cekilemedi durumunu
 * ENGELLEMEMEK icin — surtunme azaltma ilkesi.
 */
/**
 * Rutin kontrol pop-up'i — hem is detayindan hem ISLERIM kuyrugundan acilir.
 * §6.7: kuyruktaki kontrol satiri is detayina degil, DOGRUDAN buraya gider.
 */
export function RutinKontrol({
  jobId,
  onKapat,
  onBitti,
}: {
  jobId: string;
  onKapat: () => void;
  onBitti: () => void | Promise<void>;
}) {
  const [adim, setAdim] = useState<'sor' | 'foto' | 'bitti'>('sor');
  /**
   * ⚠️ Bu liste GERÇEKTEN YÜKLENMİŞ fotoğrafların numaralarıdır.
   *    Önceden yalnız bir sayaçtı: ekran "3 foto" diyordu ama hiçbiri
   *    kaydedilmiyordu — haftalık kontrolün görsel kanıtı yoktu.
   */
  const [fotolar, setFotolar] = useState<number[]>([]);
  const [gerekce, setGerekce] = useState('');
  const [kaldirildi, setKaldirildi] = useState(false);
  const [mesgul, setMesgul] = useState(false);

  /** Yüklenen rutin kontrol fotoğraflarını say — modal açıkken taze tut. */
  const fotolariYukle = useCallback(async () => {
    const d = await api
      .get<{ fotolar: { fotoNo: number; step: string; cekilmeZamani: string }[] }>(`/isler/${jobId}`)
      .catch(() => null);
    if (!d) return;
    // Yalnız BU OTURUMDA çekilenler: iş aylardır ayakta olabilir ve önceki
    // haftaların kontrol fotoğrafları da aynı adımda duruyor.
    const sinir = Date.now() - 30 * 60_000;
    setFotolar(
      d.fotolar
        .filter((f) => f.step === 'rutin_kontrol' && new Date(f.cekilmeZamani).getTime() >= sinir)
        .map((f) => f.fotoNo),
    );
  }, [jobId]);

  async function kaydet(sonuc: 'uygun' | 'yesil_kart_kaldirildi') {
    setMesgul(true);
    try {
      await api.post(`/isler/${jobId}/rutin-kontrol`, {
        clientCommandId: crypto.randomUUID(),
        olayZamani: new Date().toISOString(),
        sonuc,
        fotoSayisi: fotolar.length,
        /*
         * NUMARALAR DA GİDİYOR — yalnız sayaç değil. Yeşil kart kaldırma maili
         * fotoğrafların KENDİSİNİ taşıyor; müşteri "neden kaldırıldı"yı
         * görselden anlıyor. Sunucu numaraları bu işe ait mi diye doğruluyor.
         */
        fotoNolar: fotolar,
        not: gerekce.trim() || null,
      });
      await onBitti();
      setKaldirildi(sonuc === 'yesil_kart_kaldirildi');
      setAdim('bitti');
    } finally {
      setMesgul(false);
    }
  }

  useEffect(() => {
    void fotolariYukle();
  }, [fotolariYukle]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* Tek ekrana toplanınca içerik uzadı — telefonda taşmasın diye kaydırılabilir. */}
      <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-(--radius-kart) bg-surface p-5 shadow-(--shadow-mod-h)">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-semibold text-navy">Haftalık Kontrol</h3>
          <button onClick={onKapat} className="text-wait">
            <KapatIkon boyut={16} />
          </button>
        </div>

        {adim === 'sor' && (
          <>
            <p className="mb-1 text-sm">Kontrol gerçekleştirdim.</p>
            {/* "Her şey yolunda" MAİL ÜRETMEZ — yalnız kaldırma üretir. */}
            <p className="mb-3 text-xs text-wait">
              Uygunsa kimseye mail gitmez; yalnız yeşil kart kaldırılırsa bildirim gider.
            </p>
            <button
              onClick={() => setAdim('foto')}
              className="mb-2 w-full rounded-(--radius-mod) bg-ok px-4 py-3 text-sm font-medium text-white"
            >
              ✓ Her şey yolunda
            </button>
            {/*
              GÜVENLİK DÖNGÜSÜ — panoda KRİTİK uyarı üretir.

              ⚠️ HEPSİ TEK EKRANDA. Fotoğraf bir süre AYRI bir adımdaydı: kırmızı
                 düğmeye basılınca açılıyordu. Ama o düğme "bitti" gibi okunuyor;
                 kimse arkasında bir adım daha olduğunu tahmin etmiyor ve
                 fotoğraf seçeneği HİÇ GÖRÜLMÜYORDU. Fotoğraf zaten opsiyonel —
                 gerekçenin hemen altında durması gerekiyordu.
            */}
            <div className="mt-4 border-t border-line pt-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-crit">
                <UyariIkon boyut={12} /> Uygunsuzluk var — yeşil kartı kaldır
              </p>
              <input
                className="mb-2 w-full rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm"
                placeholder="Kaldırma gerekçesi… (zorunlu)"
                value={gerekce}
                onChange={(e) => setGerekce(e.target.value)}
              />

              <p className="mb-1.5 text-xs text-wait">
                Uygunsuzluğun fotoğrafı <strong>(isteğe bağlı)</strong> — müşteriye giden
                mailde teslimdeki hâliyle <strong>yan yana</strong> görünür.
              </p>
              {fotolar.length > 0 && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  {fotolar.map((no) => (
                    <span
                      key={no}
                      className="num inline-flex items-center gap-1 rounded-(--radius-mod) border border-ok-line bg-ok-bg px-2.5 py-1.5 text-xs text-ok"
                    >
                      <KameraIkon boyut={12} /> {no}
                    </span>
                  ))}
                </div>
              )}
              <div className="mb-3">
                <FotoEkle
                  jobId={jobId}
                  step="rutin_kontrol"
                  etiket={fotolar.length ? 'Bir fotoğraf daha' : 'Uygunsuzluğu fotoğrafla'}
                  gorunum="kompakt"
                  onBitti={fotolariYukle}
                />
              </div>

              {/*
                MAİL UYARISI DÜĞMEDEN ÖNCE. Kaldırma müşteriye giden bir bildirim
                üretiyor ve bu geri alınamaz — şef basmadan ÖNCE bilmeli.
              */}
              {/*
                ⚠️ BİLDİRİMİ TETİKLEYEN ŞEY KARTIN KALDIRILMASIDIR, FOTOĞRAF DEĞİL.
                   Düğme bir süre fotoğraf varken "kaldır ve bildir", yokken
                   sadece "kaldır" diyordu — bu "fotoğraf varsa bildirilir" gibi
                   okunuyordu. Yanlış: mail her kaldırmada gider, gerekçeyle;
                   fotoğraf varsa maile EKLENİR, yoksa mail yine de gider.
                   Metin de düğme de artık bunu sabit söylüyor.
              */}
              <p className="mb-2 text-xs leading-relaxed text-wait">
                Kaydedince işin <strong>ilgili kişilerine</strong> “yeşil kart kaldırıldı”
                maili <strong>gerekçesiyle birlikte</strong> gider
                {fotolar.length
                  ? ` — yüklediğiniz ${fotolar.length} fotoğraf da maile eklenir.`
                  : '. Fotoğraf yüklemezseniz mail yine gider, yalnız görsel karşılaştırma olmaz.'}
              </p>
              <button
                disabled={mesgul || gerekce.trim().length < 3}
                onClick={() => void kaydet('yesil_kart_kaldirildi')}
                className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-(--radius-mod) bg-crit px-4 py-3 text-sm font-medium text-white disabled:opacity-40"
              >
                <UyariIkon boyut={16} /> Yeşil kartı kaldır ve bildir
              </button>
            </div>
          </>
        )}

        {adim === 'foto' && (
          <>
            <p className="mb-3 text-sm">
              Kontrol fotoğrafı ekle <span className="text-wait">(birden çok)</span>
            </p>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {fotolar.map((no) => (
                <span
                  key={no}
                  className="num inline-flex items-center gap-1 rounded-(--radius-mod) border border-ok-line bg-ok-bg px-2.5 py-1.5 text-xs text-ok"
                >
                  <KameraIkon boyut={12} /> {no}
                </span>
              ))}
            </div>
            <div className="mb-4">
              <FotoEkle
                jobId={jobId}
                step="rutin_kontrol"
                etiket={fotolar.length ? 'Bir fotoğraf daha' : 'Kontrol fotoğrafı çek'}
                onBitti={fotolariYukle}
              />
            </div>
            <div className="flex items-center justify-between">
              {/* "geç": foto çekilemedi durumunu engelleme — sürtünme azaltma */}
              <button
                disabled={mesgul}
                onClick={() => void kaydet('uygun')}
                className="text-sm text-wait underline"
              >
                geç
              </button>
              <button
                disabled={mesgul || fotolar.length === 0}
                onClick={() => void kaydet('uygun')}
                className="rounded-(--radius-mod) bg-navy px-5 py-2.5 text-sm font-medium text-white disabled:opacity-30"
              >
                tamam
              </button>
            </div>
          </>
        )}

        {adim === 'bitti' && (
          <>
            {kaldirildi ? (
              <p className="mb-4 text-sm text-ink-2">
                <strong className="text-crit">Yeşil kart kaldırıldı.</strong> İskele artık
                “kullanıma uygun değil” olarak işaretli ve panoda kritik uyarı üretiyor.
                İlgili kişilere bildirim maili kuyruğa alındı.
              </p>
            ) : (
              <p className="mb-4 text-sm text-ink-2">
                Haftalık kontrol <strong>{new Date().toLocaleDateString('tr-TR')}</strong> itibariyle
                yapılmıştır.
              </p>
            )}
            <button
              onClick={onKapat}
              className={`flex w-full items-center justify-center gap-1.5 rounded-(--radius-mod) px-4 py-3 text-sm font-medium text-white ${
                kaldirildi ? 'bg-navy' : 'bg-ok'
              }`}
            >
              <OnayIkon boyut={16} /> Tamam
            </button>
          </>
        )}
      </div>
    </div>
  );
}
